/**
 * ASK to Draft Parser
 * Converts ASK mode responses into structured Draft sections.
 * Ported from front-react/src/lib/askToDraftParser.ts
 *
 * Diagram fences (```mermaid, ```chart-data) are extracted into rawContent
 * so DraftCanvasDoc can render them as visual diagrams in iframes.
 */

export interface ParsedSection {
  id: string;
  title: string;
  content: string;
  rawContent?: string;
  level: number;
}

/** Regex for mermaid and chart-data fenced code blocks */
const DIAGRAM_FENCE_RE = /```(?:mermaid|chart-data[^\n]*)\s*\n[\s\S]*?```/gi;

/**
 * Strip diagram fences from markdown, returning cleaned text only.
 */
function stripDiagramFences(md: string): string {
  return md.replace(DIAGRAM_FENCE_RE, "").replace(/\n{3,}/g, "\n\n").trim();
}

/**
 * Convert markdown to HTML for ReactQuill (diagram fences already stripped).
 */
function markdownToHtml(markdown: string): string {
  let html = markdown;

  // Convert headers (but not in section titles - they're already extracted)
  html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>');

  // Convert bold
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');

  // Convert italic
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  html = html.replace(/_(.+?)_/g, '<em>$1</em>');

  // Convert code blocks (non-diagram fences only — diagrams already stripped)
  html = html.replace(/```[\s\S]*?```/g, (match) => {
    const code = match.replace(/```\w*\n?/g, '').replace(/```$/g, '');
    return `<pre><code>${code}</code></pre>`;
  });

  // Convert inline code
  html = html.replace(/`(.+?)`/g, '<code>$1</code>');

  // Convert links
  html = html.replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2">$1</a>');

  // Convert unordered lists
  html = html.replace(/^\* (.+)$/gm, '<li>$1</li>');
  html = html.replace(/^- (.+)$/gm, '<li>$1</li>');
  html = html.replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>');

  // Convert ordered lists
  html = html.replace(/^\d+\. (.+)$/gm, '<li>$1</li>');

  // Convert markdown tables to HTML
  html = html.replace(
    /^(\|.+\|)\n(\|[-| :]+\|)\n((?:\|.+\|\n?)+)/gm,
    (_match, headerRow, _separator, bodyRows) => {
      const headers = headerRow
        .split("|")
        .filter((c: string) => c.trim() !== "")
        .map((c: string) => `<th>${c.trim()}</th>`)
        .join("");
      const rows = bodyRows
        .trim()
        .split("\n")
        .map((row: string) => {
          const cells = row
            .split("|")
            .filter((c: string) => c.trim() !== "")
            .map((c: string) => `<td>${c.trim()}</td>`)
            .join("");
          return `<tr>${cells}</tr>`;
        })
        .join("");
      return `<table><thead><tr>${headers}</tr></thead><tbody>${rows}</tbody></table>`;
    }
  );

  // Convert line breaks to paragraphs
  const lines = html.split('\n');
  const paragraphs: string[] = [];
  let currentParagraph = '';

  for (const line of lines) {
    const trimmed = line.trim();

    // Skip if it's already an HTML tag
    if (trimmed.startsWith('<') || trimmed === '') {
      if (currentParagraph) {
        paragraphs.push(`<p>${currentParagraph}</p>`);
        currentParagraph = '';
      }
      if (trimmed) {
        paragraphs.push(trimmed);
      }
    } else {
      if (currentParagraph) {
        currentParagraph += ' ';
      }
      currentParagraph += trimmed;
    }
  }

  if (currentParagraph) {
    paragraphs.push(`<p>${currentParagraph}</p>`);
  }

  return paragraphs.join('\n');
}

/**
 * Parse ASK response content into Draft sections.
 * Uses multiple strategies to detect section structure:
 *   1. Markdown headers (# ## ###)
 *   2. Numbered bold lists (1. **Title**)
 *   3. Single section fallback
 */
export function parseMessageIntoSections(content: string): ParsedSection[] {
  const sections: ParsedSection[] = [];

  // Strategy 1: Parse markdown headers (# ## ###)
  const headerRegex = /^(#{1,3})\s+(.+)$/gm;
  const matches = [...content.matchAll(headerRegex)];

  if (matches.length > 0) {
    let lastIndex = 0;

    matches.forEach((match, index) => {
      const level = match[1].length; // # = 1, ## = 2, ### = 3
      const title = match[2].trim();
      const startIndex = match.index!;

      // Add previous section's content
      if (index > 0) {
        const prevSection = sections[index - 1];
        prevSection.content = content
          .substring(lastIndex, startIndex)
          .trim();
      }

      // Create new section
      sections.push({
        id: `s${level}-${index}`,
        title: title,
        content: "", // Will be filled in next iteration
        level: level,
      });

      lastIndex = startIndex + match[0].length;
    });

    // Add last section's content
    if (sections.length > 0) {
      sections[sections.length - 1].content = content
        .substring(lastIndex)
        .trim();
    }

    return sections;
  }

  // Strategy 2: Parse numbered lists (1. 2. 3.)
  const numberedRegex = /^(\d+)\.\s+\*\*(.+?)\*\*/gm;
  const numberedMatches = [...content.matchAll(numberedRegex)];

  if (numberedMatches.length > 0) {
    let lastIndex = 0;

    numberedMatches.forEach((match, index) => {
      const title = match[2].trim();
      const startIndex = match.index!;

      // Add previous section's content
      if (index > 0) {
        const prevSection = sections[index - 1];
        const endIndex = startIndex;
        prevSection.content = content
          .substring(lastIndex, endIndex)
          .trim()
          .replace(/^\d+\.\s+\*\*.+?\*\*\s*/, ""); // Remove title from content
      }

      // Create new section
      sections.push({
        id: `s1-${index}`,
        title: title,
        content: "",
        level: 1,
      });

      lastIndex = startIndex;
    });

    // Add last section's content
    if (sections.length > 0) {
      sections[sections.length - 1].content = content
        .substring(lastIndex)
        .trim()
        .replace(/^\d+\.\s+\*\*.+?\*\*\s*/, "");
    }

    return sections;
  }

  // Strategy 3: Single section fallback
  sections.push({
    id: "s1-0",
    title: "Content",
    content: content.trim(),
    level: 1,
  });

  return sections;
}

/**
 * Generate a title from the first line or first section
 */
export function extractTitle(
  content: string,
  fallback: string = "Untitled Document"
): string {
  // Try to find first header
  const headerMatch = content.match(/^#\s+(.+)$/m);
  if (headerMatch) {
    return headerMatch[1].trim();
  }

  // Try to find first bold text
  const boldMatch = content.match(/\*\*(.+?)\*\*/);
  if (boldMatch) {
    return boldMatch[1].trim();
  }

  // Use first line (truncated)
  const firstLine = content.split("\n")[0].trim();
  if (firstLine.length > 0) {
    return (
      firstLine.substring(0, 60) + (firstLine.length > 60 ? "..." : "")
    );
  }

  return fallback;
}

/**
 * Convert section content from markdown to HTML.
 * Diagram fences are extracted into rawContent so DraftCanvasDoc
 * can render them as visual diagrams in iframes.
 */
export function convertSectionToHtml(section: ParsedSection): ParsedSection {
  const cleanTitle = section.title.replace(/\*\*/g, "").replace(/\*/g, "");

  // Extract mermaid/chart-data fences before HTML conversion
  const diagramFences = (section.content.match(DIAGRAM_FENCE_RE) || []).join("\n\n");

  // Strip diagrams from content, then convert remaining markdown to HTML
  const textOnly = stripDiagramFences(section.content);
  const html = markdownToHtml(textOnly);

  return {
    ...section,
    title: cleanTitle,
    content: html,
    rawContent: diagramFences || undefined,
  };
}
